// Synthesizer: assembles the final dossier with executive summary,
// per-sub-question sections, inline [n] citations, per-claim confidence,
// and an explicit limitations section.

import type { Claim, Dossier, DossierSection, PlanTree, RetrievedDoc, SubAgentState } from "./types";

export interface SynthesizeArgs {
  query: string;
  plan: PlanTree;
  agents: SubAgentState[];
  startedAt: number;
  totalCostUsd: number;
  totalTokens: number;
}

export function synthesizeDossier(args: SynthesizeArgs): Dossier {
  // Build a global citation index over all evidence used by accepted/weakened claims
  const evidenceMap = new Map<string, RetrievedDoc>();
  const claimsAll: Claim[] = [];
  for (const a of args.agents) {
    for (const c of a.claims) {
      if (c.status === "rejected") continue;
      claimsAll.push(c);
      for (const eid of c.evidenceIds) {
        const doc = a.evidence.find((d) => d.id === eid);
        if (doc && !evidenceMap.has(doc.id)) evidenceMap.set(doc.id, doc);
      }
    }
  }
  const citations = Array.from(evidenceMap.values());
  const citationIndex = new Map<string, number>();
  citations.forEach((d, i) => citationIndex.set(d.id, i + 1));

  // Group agents by parent (level-1 sub-question)
  const sectionsMap = new Map<string, { heading: string; agents: SubAgentState[] }>();
  for (const a of args.agents) {
    const node = args.plan.nodes[a.nodeId];
    if (!node) continue;
    // Walk up to depth 1
    let cursor = node;
    while (cursor.parentId && (args.plan.nodes[cursor.parentId]?.depth ?? -1) >= 1) {
      const parent = args.plan.nodes[cursor.parentId!];
      if (!parent || parent.depth < 1) break;
      cursor = parent;
    }
    const key = cursor.id;
    const existing = sectionsMap.get(key);
    if (existing) existing.agents.push(a);
    else sectionsMap.set(key, { heading: cursor.question, agents: [a] });
  }

  const sections: DossierSection[] = [];
  for (const [nodeId, group] of sectionsMap) {
    const groupClaims = group.agents
      .flatMap((a) => a.claims)
      .filter((c) => c.status !== "rejected")
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 6);

    const lines: string[] = [];
    for (const c of groupClaims) {
      const cites = c.evidenceIds.map((eid) => citationIndex.get(eid)).filter(Boolean) as number[];
      const citationStr = cites.length ? ` ${cites.map((n) => `[${n}]`).join(" ")}` : "";
      const confTag = c.confidence >= 0.75 ? "🟢" : c.confidence >= 0.5 ? "🟡" : "🔴";
      lines.push(`- ${confTag} ${c.text}${citationStr}`);
    }

    sections.push({
      nodeId,
      heading: group.heading,
      body: lines.length ? lines.join("\n") : "_No supported claim survived adversarial review for this sub-question._",
      claims: groupClaims,
    });
  }

  // Executive summary: top 3 highest-confidence accepted claims overall
  const topAccepted = claimsAll
    .filter((c) => c.status === "accepted")
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 3);
  const executiveSummary = topAccepted.length
    ? topAccepted
        .map((c, i) => {
          const cites = c.evidenceIds.map((eid) => citationIndex.get(eid)).filter(Boolean) as number[];
          const citationStr = cites.length ? ` ${cites.map((n) => `[${n}]`).join(" ")}` : "";
          return `${i + 1}. ${c.text}${citationStr}`;
        })
        .join("\n")
    : "Insufficient evidence converged across sub-agents to assert a high-confidence summary.";

  // Limitations: aggregate notable rationales + counts of weakened/rejected
  const allClaims = args.agents.flatMap((a) => a.claims);
  const rejectedN = allClaims.filter((c) => c.status === "rejected").length;
  const weakenedN = allClaims.filter((c) => c.status === "weakened").length;
  const limitations: string[] = [];
  if (rejectedN) limitations.push(`${rejectedN} proposed claim(s) were rejected during adversarial review.`);
  if (weakenedN) limitations.push(`${weakenedN} claim(s) were retained but weakened — treat as suggestive.`);
  const distinctRationales = Array.from(
    new Set(
      allClaims
        .filter((c) => c.rationale)
        .flatMap((c) => c.rationale!.split(";").map((s) => s.trim()))
        .filter(Boolean),
    ),
  ).slice(0, 5);
  for (const r of distinctRationales) limitations.push(`Recurring critique: ${r}.`);
  if (!limitations.length) limitations.push("No critic-level objections recorded; still treat as preliminary synthesis.");

  const overallConfidence =
    args.agents.length === 0
      ? 0
      : args.agents.reduce((s, a) => s + a.confidence, 0) / args.agents.length;

  const endedAt = performance.now();
  return {
    query: args.query,
    executiveSummary,
    sections,
    citations,
    limitations,
    overallConfidence,
    startedAt: args.startedAt,
    endedAt,
    totalCostUsd: args.totalCostUsd,
    totalLatencyMs: endedAt - args.startedAt,
    totalTokens: args.totalTokens,
  };
}
